import {
  EexConnectionMutationResponseSchema,
  EexConnectionResponseSchema,
  EexConnectionStatusOnlyResponseSchema,
  EexConnectionUpdateRequestSchema,
  type EexConnectionResponse as ConnectionResponse,
  type EexConnectionStatus as ConnectionStatus,
} from "@chro/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Activity, KeyRound, Trash2 } from "lucide-react";
import { useState } from "react";
import { useEexRole } from "../components/eex/use-eex-role";
import { LiveMarketPanel } from "../components/eex/market-data/LiveMarketPanel";
import { ScopeOnboardingPanel } from "../components/eex/market-data/ScopeOnboardingPanel";
import { ConformanceRolloutPanel } from "../components/eex/market-data/ConformanceRolloutPanel";
import { CompanyLiveUniversePanel } from "../components/eex/market-data/CompanyLiveUniversePanel";
import { DesktopOnlyNotice } from "../components/ui/desktop-only-notice";
import { useMobileUiV1State } from "../hooks/use-media-query";
import { api, apiContract } from "../lib/api";
import {
  canPromotePendingEexCredential,
  canTestEexDatasourceConnection,
  canToggleEexDatasourceConnection,
  eexConnectionTestIsActive,
  eexCredentialStorePayload,
} from "../lib/eex-datasource-controls";
import { cn } from "../lib/utils";

export const Route = createFileRoute("/eex/market-data")({
  component: EexMarketDataRoute,
});

type ConnectionLifecycle = ConnectionStatus["lifecycle"];

function EexMarketDataRoute() {
  const { presenter } = useMobileUiV1State();

  if (presenter === "desktop") return <EexMarketDataPage />;
  if (presenter === "mobile") return <MobileEexMarketDataPage />;

  return (
    <div className="mx-auto max-w-lg px-4 py-8">
      <DesktopOnlyNotice minimumWidth={360} />
    </div>
  );
}

function MobileEexMarketDataPage() {
  return (
    <div className="min-w-0 space-y-4 p-3">
      <MarketDataHeader />
      <LiveMarketPanel mobileReadOnly />
    </div>
  );
}

function EexMarketDataPage() {
  const { role } = useEexRole();
  const isCompanyAdmin = role === "company_admin";
  const queryClient = useQueryClient();
  const [token, setToken] = useState("");
  const [tokenExpiresAtLocal, setTokenExpiresAtLocal] = useState("");
  const [feedback, setFeedback] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const connectionQ = useQuery({
    queryKey: ["eex-market-data", "connection"],
    queryFn: () => apiContract(EexConnectionResponseSchema, "/eex/market-data/connection"),
    enabled: isCompanyAdmin,
    retry: false,
    refetchInterval: (query) => {
      const current = query.state.data;
      return current && eexConnectionTestIsActive(current.status) ? 2_000 : false;
    },
  });
  const connection = connectionQ.data;

  const invalidateConnection = () =>
    queryClient.invalidateQueries({ queryKey: ["eex-market-data", "connection"] });

  const saveM = useMutation({
    mutationFn: ({ value, expiresAtLocal }: { value: string; expiresAtLocal: string }) =>
      apiContract(EexConnectionMutationResponseSchema, "/eex/market-data/connection", {
        method: "PUT",
        body: JSON.stringify(EexConnectionUpdateRequestSchema.parse(
          eexCredentialStorePayload(value, expiresAtLocal)
        )),
      }),
    onSuccess: ({ status }) => {
      setToken("");
      setTokenExpiresAtLocal("");
      setError(null);
      setFeedback(
        status.pendingReplacement
          ? "Replacement token stored as pending. The current connection is unchanged until the replacement is tested and promoted."
          : "Token stored with the connection disabled. Test it before explicitly enabling market-data acquisition."
      );
      void invalidateConnection();
    },
    onError: showError(setError),
  });

  const toggleM = useMutation({
    mutationFn: (enabled: boolean) =>
      apiContract(EexConnectionMutationResponseSchema, "/eex/market-data/connection", {
        method: "PUT",
        body: JSON.stringify(EexConnectionUpdateRequestSchema.parse({ enabled })),
      }),
    onSuccess: (_data, enabled) => {
      setError(null);
      setFeedback(
        enabled
          ? "Company connection enabled."
          : "Company connection disabled. Manual quotes, pasted trades and reconciliation remain unchanged."
      );
      void invalidateConnection();
    },
    onError: showError(setError),
  });

  const testM = useMutation({
    mutationFn: () =>
      api("/eex/market-data/connection/test", {
        method: "POST",
      }),
    onSuccess: () => {
      setError(null);
      setFeedback("Connection test queued. Status will update when the private worker completes it.");
      void invalidateConnection();
    },
    onError: showError(setError),
  });

  const attestM = useMutation({
    mutationFn: () =>
      apiContract(EexConnectionStatusOnlyResponseSchema, "/eex/market-data/connection/quota-principal", {
        method: "POST",
        body: JSON.stringify({ newEexUser: true }),
      }),
    onSuccess: () => {
      setError(null);
      setFeedback(
        "This connection now has its own explicitly attested EEX-user request budget. You can queue the connection test."
      );
      void invalidateConnection();
    },
    onError: showError(setError),
  });

  const promoteM = useMutation({
    mutationFn: () =>
      apiContract(EexConnectionStatusOnlyResponseSchema, "/eex/market-data/connection/promote", {
        method: "POST",
      }),
    onSuccess: () => {
      setError(null);
      setFeedback("Validated replacement promoted atomically. The previous credential is no longer active.");
      void invalidateConnection();
    },
    onError: showError(setError),
  });

  const removeM = useMutation({
    mutationFn: () => apiContract(
      EexConnectionMutationResponseSchema,
      "/eex/market-data/connection",
      { method: "DELETE" }
    ),
    onSuccess: () => {
      setToken("");
      setTokenExpiresAtLocal("");
      setError(null);
      setFeedback("Stored token and any pending replacement removed. The connection is disabled.");
      void invalidateConnection();
    },
    onError: showError(setError),
  });

  const busy =
    saveM.isPending ||
    toggleM.isPending ||
    testM.isPending ||
    attestM.isPending ||
    promoteM.isPending ||
    removeM.isPending;

  if (!isCompanyAdmin) {
    return (
      <PageFrame>
        <LiveMarketPanel canAdminister={false} />
      </PageFrame>
    );
  }

  return (
    <PageFrame>
      <CompanyLiveUniversePanel />
      <LiveMarketPanel canAdminister />
      {connectionQ.isLoading ? (
        <div className="text-sm text-muted">Loading connection…</div>
      ) : connectionQ.isError ? (
        <div className="text-sm text-negRed">Unable to load connection: {(connectionQ.error as Error).message}</div>
      ) : connection ? (
        <section className="max-w-3xl space-y-4 rounded-lg border border-border p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 text-sm font-semibold">
                <KeyRound size={16} /> DataSource API credentials
              </div>
              <p className="mt-1 max-w-2xl text-xs text-muted">
                Saving a token never enables acquisition. A new token is stored disabled; a replacement remains
                pending until the private worker tests it and a company administrator explicitly promotes it.
              </p>
            </div>
            <ConnectionBadge connection={connection} />
          </div>

          {!connection.masterEnabled && (
            <div className="rounded border border-amber/40 bg-amber/5 p-2 text-xs text-amber">
              EEX DataSource acquisition is globally off. Tokens may be stored safely, but tests and outbound requests
              remain disabled. Existing manual workflows continue normally.
            </div>
          )}

          {connection.masterEnabled && !connection.workerAvailable && (
            <div className="rounded border border-amber/40 bg-amber/5 p-2 text-xs text-amber">
              The private EEX worker is unavailable. Tokens may still be stored, but connection testing and enablement
              remain paused. Existing manual workflows continue normally.
            </div>
          )}

          {!connection.credentialStorageAvailable && (
            <div className="rounded border border-amber/40 bg-amber/5 p-2 text-xs text-amber">
              Credential storage is paused because the worker public sealing key is not configured. This does not
              affect any existing manual workflow.
            </div>
          )}

          <ConnectionDetails status={connection.status} />

          {connection.status.credentialWarning && (
            <div
              className={cn(
                "rounded border p-2 text-xs",
                connection.status.activeExpiryState === "expired" ||
                  connection.status.pendingExpiryState === "expired"
                  ? "border-negRed/40 bg-negRed/5 text-negRed"
                  : "border-amber/40 bg-amber/5 text-amber"
              )}
            >
              {connection.status.credentialWarning}
            </div>
          )}

          {connection.canEdit ? (
            <>
              <label className="block text-xs text-muted">
                {connection.status.configured || connection.status.pendingReplacement
                  ? "Store replacement access token"
                  : "Access token"}
              </label>
              <input
                type="password"
                value={token}
                disabled={busy || !connection.credentialStorageAvailable}
                autoComplete="new-password"
                onChange={(event) => setToken(event.target.value)}
                placeholder={
                  connection.status.configured || connection.status.pendingReplacement
                    ? "paste a replacement token"
                    : "paste EEX bearer token"
                }
                className="w-full rounded border border-border bg-transparent px-2 py-1.5 text-sm focus:border-accent focus:outline-none"
              />
              <label className="block text-xs text-muted">
                Token expiry (optional, in your local time)
              </label>
              <input
                type="datetime-local"
                value={tokenExpiresAtLocal}
                disabled={busy || !connection.credentialStorageAvailable}
                onChange={(event) => setTokenExpiresAtLocal(event.target.value)}
                className="w-full rounded border border-border bg-transparent px-2 py-1.5 text-sm focus:border-accent focus:outline-none"
              />
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={busy || !connection.credentialStorageAvailable || !token.trim()}
                  onClick={() =>
                    saveM.mutate({ value: token.trim(), expiresAtLocal: tokenExpiresAtLocal })
                  }
                  className="rounded bg-accent px-3 py-1.5 text-sm text-bg disabled:opacity-40"
                >
                  Store disabled
                </button>
                {connection.status.configured && !connection.status.quotaPrincipalAssigned && (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => {
                      if (
                        confirm(
                          "Confirm that this token belongs to a new EEX DataSource Hub user, not an EEX user already connected for another company."
                        )
                      ) {
                        attestM.mutate();
                      }
                    }}
                    className="rounded border border-border px-3 py-1.5 text-sm disabled:opacity-40"
                  >
                    {attestM.isPending ? "Attesting…" : "Attest new EEX user"}
                  </button>
                )}
                <button
                  type="button"
                  disabled={
                    busy ||
                    !canTestEexDatasourceConnection({
                      masterEnabled: connection.masterEnabled,
                      workerAvailable: connection.workerAvailable,
                      configured: connection.status.configured,
                      pendingReplacement: connection.status.pendingReplacement,
                      quotaPrincipalAttested: connection.status.quotaPrincipalAttested,
                      pendingQuotaPrincipalAttested: connection.status.pendingQuotaPrincipalAttested,
                      testInProgress: connection.status.testInProgress,
                      pendingTestInProgress: connection.status.pendingTestInProgress,
                      activeExpiryState: connection.status.activeExpiryState,
                      pendingExpiryState: connection.status.pendingExpiryState,
                    })
                  }
                  onClick={() => testM.mutate()}
                  className="inline-flex items-center gap-1.5 rounded border border-border px-3 py-1.5 text-sm disabled:opacity-40"
                >
                  <Activity size={14} />{" "}
                  {testM.isPending
                    ? "Queuing…"
                    : connection.status.pendingReplacement
                      ? "Test pending replacement"
                      : "Test access"}
                </button>
                {connection.status.pendingReplacement && (
                  <button
                    type="button"
                    disabled={
                      busy ||
                      !canPromotePendingEexCredential({
                        pendingReplacement: connection.status.pendingReplacement,
                        pendingQuotaPrincipalAttested: connection.status.pendingQuotaPrincipalAttested,
                        pendingLastTestStatus: connection.status.pendingLastTestStatus,
                        pendingTestInProgress: connection.status.pendingTestInProgress,
                        pendingExpiryState: connection.status.pendingExpiryState,
                      })
                    }
                    onClick={() => {
                      if (confirm("Promote the successfully tested replacement EEX token now?")) {
                        promoteM.mutate();
                      }
                    }}
                    className="rounded border border-border px-3 py-1.5 text-sm disabled:opacity-40"
                  >
                    {promoteM.isPending ? "Promoting…" : "Promote replacement"}
                  </button>
                )}
                <button
                  type="button"
                  disabled={
                    busy ||
                    !canToggleEexDatasourceConnection({
                      enabled: connection.status.enabled,
                      masterEnabled: connection.masterEnabled,
                      workerAvailable: connection.workerAvailable,
                      credentialCanEnable: connection.status.canEnable,
                    })
                  }
                  onClick={() => toggleM.mutate(!connection.status.enabled)}
                  className="rounded border border-border px-3 py-1.5 text-sm disabled:opacity-40"
                >
                  {connection.status.enabled ? "Disable" : "Enable"}
                </button>
                <button
                  type="button"
                  disabled={busy || (!connection.status.configured && !connection.status.pendingReplacement)}
                  onClick={() => {
                    if (confirm("Remove the stored EEX DataSource token and any pending replacement?")) {
                      removeM.mutate();
                    }
                  }}
                  className="inline-flex items-center gap-1.5 rounded border border-border px-3 py-1.5 text-sm text-negRed disabled:opacity-40"
                >
                  <Trash2 size={14} /> Remove
                </button>
              </div>
              {!connection.status.enabled && connection.status.enablementBlockReason && (
                <div className="text-xs text-muted">
                  Enable unavailable: {connection.status.enablementBlockReason}
                </div>
              )}
            </>
          ) : (
            <div className="text-xs text-muted">Credential administration is unavailable for this company scope.</div>
          )}

          {connection.status.lastTestStatus &&
            connection.status.lastTestStatus !== "ok" &&
            !connection.status.testInProgress && (
              <div className="text-xs text-negRed">
                Last test: {connection.status.lastTestStatus}
                {connection.status.lastTestError ? ` — ${connection.status.lastTestError}` : ""}
              </div>
            )}
          {connection.status.testInProgress && (
            <div className="text-xs text-muted">Active-token connection test is in progress.</div>
          )}
          {connection.status.pendingLastTestStatus &&
            connection.status.pendingLastTestStatus !== "ok" &&
            !connection.status.pendingTestInProgress && (
              <div className="text-xs text-negRed">
                Pending-token test: {connection.status.pendingLastTestStatus}
                {connection.status.pendingLastTestError ? ` — ${connection.status.pendingLastTestError}` : ""}
              </div>
            )}
          {connection.status.pendingTestInProgress && (
            <div className="text-xs text-muted">Pending-replacement connection test is in progress.</div>
          )}
          {feedback && <div className="text-xs text-posGreen">{feedback}</div>}
          {error && <div className="text-xs text-negRed">{error}</div>}
        </section>
      ) : null}
      {connection && (
        <ScopeOnboardingPanel
          connectionReady={
            connection.masterEnabled &&
            connection.workerAvailable &&
            connection.status.enabled &&
            connection.status.lastTestStatus === "ok" &&
            connection.status.quotaPrincipalAttested &&
            connection.status.activeExpiryState !== "expired"
          }
        />
      )}
      <ConformanceRolloutPanel />
    </PageFrame>
  );
}

function PageFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="space-y-6 p-4">
      <MarketDataHeader />
      {children}
    </div>
  );
}

function MarketDataHeader() {
  return (
    <header>
      <h1 className="text-lg font-semibold">EEX Market Data</h1>
      <p className="max-w-3xl text-xs text-muted">
        Optional read-only EEX DataSource connection for entitled power, natural-gas and index datasets. It does
        not replace manual quotes, pasted trades, bank statements or ECC settlement processing.
      </p>
    </header>
  );
}

function ConnectionDetails({ status }: { status: ConnectionStatus }) {
  return (
    <div className="space-y-1 text-xs text-muted">
      <div>Lifecycle: {formatLifecycle(status.lifecycle)}</div>
      <div>
        {status.configured
          ? `Stored token${status.last4 ? ` · ending …${status.last4}` : ""}`
          : "No active token stored."}
      </div>
      {status.configured && (
        <div>
          Active expiry: {formatCredentialExpiry(status.activeTokenExpiresAt, status.activeExpiryState)}
        </div>
      )}
      {status.configured && (
        <div>
          EEX-user quota: {status.quotaPrincipalAttested ? "explicitly attested" : "attestation required"}
        </div>
      )}
      {status.pendingReplacement && (
        <>
          <div>
            Pending replacement{status.pendingLast4 ? ` · ending …${status.pendingLast4}` : ""}
          </div>
          <div>
            Pending expiry: {formatCredentialExpiry(status.pendingTokenExpiresAt, status.pendingExpiryState)}
          </div>
          <div>
            Pending EEX-user quota: {status.pendingQuotaPrincipalAttested ? "inherited" : "attestation required"}
          </div>
        </>
      )}
      {status.lastTestedAt && <div>Last tested {new Date(status.lastTestedAt).toLocaleString()}</div>}
      {status.pendingLastTestedAt && (
        <div>Pending replacement tested {new Date(status.pendingLastTestedAt).toLocaleString()}</div>
      )}
    </div>
  );
}

function ConnectionBadge({ connection }: { connection: ConnectionResponse }) {
  const label = !connection.masterEnabled
    ? "server off"
    : !connection.workerAvailable
      ? "worker unavailable"
      : connection.status.activeExpiryState === "expired"
        ? "credential expired"
      : formatLifecycle(connection.status.lifecycle);
  const good =
    connection.masterEnabled &&
    connection.workerAvailable &&
    connection.status.lifecycle === "enabled" &&
    connection.status.activeExpiryState !== "expired";
  return (
    <span
      className={cn(
        "rounded border px-2 py-0.5 text-xs",
        good ? "border-posGreen/40 text-posGreen" : "border-border text-muted"
      )}
    >
      {label}
    </span>
  );
}

function formatLifecycle(value: ConnectionLifecycle): string {
  return value.replaceAll("_", " ");
}

function formatCredentialExpiry(
  expiresAt: string | null,
  state: ConnectionStatus["activeExpiryState"]
): string {
  if (!expiresAt) return "not recorded";
  const suffix = state === "expiring_soon" ? " · expires soon" : state === "expired" ? " · expired" : "";
  return `${new Date(expiresAt).toLocaleString()}${suffix}`;
}

function showError(setter: (value: string | null) => void) {
  return (value: unknown) => setter(value instanceof Error ? value.message : String(value));
}
